import { useCallback, useState } from "react";
// Import directo (no el barrel ../../services) para no crear un ciclo
// services -> hooks -> services.
import { apiFetch } from "../../services/services.props";
import {
  ApiResponse,
  ERROR_FETCH_DATA,
  ErrorType,
  getInitialData,
  HTTP_SUCCESS_MAX,
  HTTP_SUCCESS_MIN,
} from "./useApiFetch.props";

const isSuccessStatus = (code?: number): boolean =>
  code !== undefined && code >= HTTP_SUCCESS_MIN && code <= HTTP_SUCCESS_MAX;

const normalizeResponse = <T,>(response: any): ApiResponse<T> => {
  if (response?.code !== undefined && !isSuccessStatus(response.code)) {
    return response;
  }
  return { data: response };
};

export function useApiFetch<T = any>(initialData?: T) {
  const [data, setData] = useState<T>(() => initialData ?? getInitialData<T>());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorType>(null);

  const callApi = useCallback(
    async (endpoint: string, options: RequestInit = {}) => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiFetch(endpoint, options);

        if (response.ok) {
          // A 204 (or any empty-body success, e.g. DELETE) has nothing to parse.
          const responseText = await response.text();
          const responseJson = responseText.length > 0 ? JSON.parse(responseText) : null;
          const responseData = normalizeResponse<T>(responseJson);

          const isError =
            responseData?.code !== undefined &&
            !isSuccessStatus(responseData.code);

          if (isError || responseData.data === undefined) {
            setError(ERROR_FETCH_DATA);
            return { code: responseData?.code, data: null };
          }

          setData(responseData.data);
          return responseData;
        } else {
          let message: string = ERROR_FETCH_DATA;
          try {
            const errorJson = await response.json();
            if (typeof errorJson?.error === "string" && errorJson.error.length) {
              message = errorJson.error;
            }
          } catch {
            // response body wasn't JSON — keep the generic error
          }
          setError(message);
          return { code: response.status, data: null };
        }
      } catch (err) {
        setError(err as ErrorType);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return [callApi, data, loading, error] as const;
}
